import { Fragment } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { formatPrice } from '@spotahome/soyuz-currency';

import { Collapsible, Tooltip, InfoIcon } from '../..';

import PaymentInfoSectionHeader from './PaymentInfoSectionHeader';

import styles from './PaymentInfo.module.scss';

const CN_PREFIX = 'payment-info';

const getCn = element => styles[`${CN_PREFIX}__${element}`];

const PaymentInfoSection = ({
  name = '',
  price = 0,
  priceWithoutOffer = null,
  currencyIsoCode = '',
  dataTest = '',
  isCollapsed = true,
  items = [],
  onCollapsedChange = () => {},
  renderBeforeSection = () => '',
  renderBeforeItemsSection = () => '',
  priceIsMonthly = false,
  payToSpotahome
}) => (
  <div>
    {renderBeforeSection()}
    <Collapsible
      isControlled={false}
      headerClassName={getCn('section')}
      renderHeader={() => (
        <PaymentInfoSectionHeader
          payToSpotahome={payToSpotahome}
          dataTest={dataTest}
          name={name}
          items={items}
          currencyIsoCode={currencyIsoCode}
          price={price}
          priceWithoutOffer={priceWithoutOffer}
          priceIsMonthly={priceIsMonthly}
        />
      )}
      renderBody={() => (
        <div className={getCn('payments')}>
          {renderBeforeItemsSection()}
          {items.map(
            ({
              show,
              name: sectionName,
              alreadyPaid,
              price: sectionPrice,
              priceLabel: sectionPriceLabel,
              hidePrice,
              discountValue,
              priceWithoutOffer: sectionPriceWithoutOffer,
              dataTest: itemDataTest,
              tooltip,
              hint
            }) =>
              show !== false ? (
                <>
                  <div
                    key={name}
                    data-test={itemDataTest}
                    className={getCn('row')}
                  >
                    <div
                      className={classNames(
                        getCn('cell'),
                        getCn('cell--name-container')
                      )}
                    >
                      <span className={getCn('cell--name-container__title')}>
                        {sectionName}
                      </span>
                      {tooltip && (
                        <Tooltip
                          className={getCn('tooltip')}
                          color="blue-indigo"
                          position="bottom"
                          tooltip={tooltip}
                        >
                          <InfoIcon />
                        </Tooltip>
                      )}
                    </div>

                    <div
                      className={classNames(
                        getCn('cell'),
                        getCn('cell--prices-container')
                      )}
                    >
                      {!!discountValue && !!sectionPriceWithoutOffer && (
                        <Fragment>
                          <span
                            className={classNames(
                              getCn('price'),
                              getCn('price--without-offer-container')
                            )}
                          >
                            <span
                              className={classNames(
                                getCn('price--without-offer')
                              )}
                            >
                              {formatPrice(
                                sectionPriceWithoutOffer,
                                currencyIsoCode
                              )}
                            </span>
                          </span>
                        </Fragment>
                      )}
                      {!hidePrice && (
                        <span
                          className={classNames(getCn('price'), {
                            [`${getCn('price--paid')}`]: alreadyPaid,
                            [`${getCn('price--with-offer')}`]: !!discountValue
                          })}
                        >
                          {sectionPriceLabel ||
                            formatPrice(sectionPrice, currencyIsoCode)}
                        </span>
                      )}
                    </div>
                  </div>
                  {hint && (
                    <div
                      key={`${name}-hint`}
                      data-test={`${itemDataTest}-hint`}
                      className={getCn('row--hint')}
                    >
                      <span className={getCn('cell--name-container__title')}>
                        {hint}
                      </span>
                    </div>
                  )}
                </>
              ) : null
          )}
        </div>
      )}
      isCollapsed={isCollapsed}
      onCollapsedChange={onCollapsedChange}
      hasIcon
    />
  </div>
);

PaymentInfoSection.propTypes = {
  name: PropTypes.string,
  price: PropTypes.number,
  priceWithoutOffer: PropTypes.number,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      price: PropTypes.number,
      priceWithoutOffer: PropTypes.number,
      tooltip: PropTypes.string,
      alreadyPaid: PropTypes.bool,
      dataTest: PropTypes.string,
      discountType: PropTypes.string,
      discountValue: PropTypes.number,
      hint: PropTypes.string
    })
  ),
  currencyIsoCode: PropTypes.string,
  dataTest: PropTypes.string,
  isCollapsed: PropTypes.bool,
  onCollapsedChange: PropTypes.func,
  renderBeforeSection: PropTypes.func,
  renderBeforeItemsSection: PropTypes.func,
  payToSpotahome: PropTypes.bool.isRequired,
  priceIsMonthly: PropTypes.bool
};

export default PaymentInfoSection;
